/**
 * @fileoverview Connect assigned-review navigation badges to the backend's current count. The hook owns refresh requests and shared Intake refresh notifications; the backend owns visibility and assignment scope. Badge refresh is independent from loading review packets or taking a review decision.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchAssignedIntakeAttorneyReviewCount } from './intakeApi'

const COUNT_UPDATED_EVENT = 'intake:assigned-attorney-review-count-updated'
const COUNT_REFRESH_EVENT = 'intake:assigned-attorney-review-count-refresh'



function normalizeCount(value) {
    const count = Number(value)
    return Number.isFinite(count) && count > 0 ? count : 0
}



export function formatNotificationCount(count) {
    const normalized = normalizeCount(count)
    return normalized > 99 ? '99+' : String(normalized)
}



export function publishAssignedAttorneyReviewCount(count) {
    window.dispatchEvent(new CustomEvent(COUNT_UPDATED_EVENT, {
        detail: { count: normalizeCount(count) },
    }))
}



export function refreshAssignedAttorneyReviewCount() {
    window.dispatchEvent(new Event(COUNT_REFRESH_EVENT))
}



export function useAssignedAttorneyReviewCount(enabled = true) {
    const mountedRef = useRef(false)
    const [count, setCount] = useState(null)

    const refresh = useCallback(

    async () => {
        if (!enabled) return;
        try {
            const nextCount = await fetchAssignedIntakeAttorneyReviewCount()
            const normalized = normalizeCount(nextCount)
            if (!mountedRef.current) return
            setCount(normalized)
            publishAssignedAttorneyReviewCount(normalized)
        } catch (error) {
            console.error('Failed to load assigned intake attorney review count:', error)
        }
    }, [enabled])

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    useEffect(() => {
        refresh()
    }, [refresh])

    useEffect(

    () => {
        const handleCountUpdated =

        (event) => {
            setCount(normalizeCount(event.detail?.count))
        }
        const handleCountRefresh =

        () => {
            refresh()
        }
        window.addEventListener(COUNT_UPDATED_EVENT, handleCountUpdated)
        window.addEventListener(COUNT_REFRESH_EVENT, handleCountRefresh)
        return () => {
            window.removeEventListener(COUNT_UPDATED_EVENT, handleCountUpdated)
            window.removeEventListener(COUNT_REFRESH_EVENT, handleCountRefresh)
        }
    }, [refresh])

    return { count, refresh }
}



export function AssignedAttorneyReviewCountChip({ count, className = '', testId }) {
    const normalized = normalizeCount(count)
    if (!normalized) return null
    return (
        <span
            aria-hidden="true"
            className={`kc-notification-chip${className ? ` ${className}` : ''}`}
            data-testid={testId}>
            {formatNotificationCount(normalized)}
        </span>
    )
}
